import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, lastValueFrom, Subscription } from "rxjs";
import { environment } from "src/environments/environment";

import ApiVersionConstants from "../constants/api-version-constants";
import { Announcement } from "../models/Announcement";
import { AuthService } from "./auth.service";
import { StorageService } from "./storage.service";

const ANNOUNCEMENT_EXPIRATION_IN_MSEC = 1000 * 60 * 60; // 1 hour
@Injectable({
  providedIn: "root",
})
export class AnnouncementService implements OnDestroy {
  private baseUrl_V2 = `${environment.apiConfig.uri}/${ApiVersionConstants.V_2}/announcements`;
  private announcementSubject = new BehaviorSubject<Announcement>(undefined);
  private _subscriptions$: Subscription[] = [];

  public announcement$ = this.announcementSubject.asObservable();

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private authService: AuthService,
  ) {
    this.getAnnouncementAsync();
    this.onLoginChange();
  }

  private async getAnnouncementAsync() {
    const shouldSkipServerCheck = this.canSkipGettingFromServer();

    let announcement: Announcement;
    if (shouldSkipServerCheck) {
      announcement = this.storageService.getLastAnnouncement();
    } else {
      announcement = await this.getAnnouncementFromServerAsync();
    }

    // if there is no announcement, nothing to do more
    if (!announcement?.announcementId) {
      return;
    }

    // if there is any pending announcement, check user local storage if this announcement has been shown to it.
    const localStorageAnnouncement = this.storageService.getSeenAnnouncements();

    // if this announcement has been already shown to user, do not show it again
    if (localStorageAnnouncement?.announcementIds.some((x) => x === announcement.announcementId)) {
      return;
    }

    this.announcementSubject.next(announcement);
  }

  private onLoginChange() {
    const subscription = this.authService.isUserProfileSet$.subscribe(() =>
      this.retrieveAnnouncementAsync(),
    );

    this._subscriptions$.push(subscription);
  }

  public async retrieveAnnouncementAsync(): Promise<void> {
    this.announcementSubject.next(undefined);
    this.storageService.clearLastAnnouncementCheckDate();
    await this.getAnnouncementAsync();
  }

  private canSkipGettingFromServer(): boolean {
    const lastCheckedAt = this.storageService.getLastAnnouncementCheckDate();
    const shouldSkipServerCheck =
      lastCheckedAt &&
      new Date().getTime() - lastCheckedAt.getTime() < ANNOUNCEMENT_EXPIRATION_IN_MSEC;
    return shouldSkipServerCheck;
  }

  private async getAnnouncementFromServerAsync(): Promise<Announcement> {
    const announcementResponse = await lastValueFrom(this.http.get<Announcement>(this.baseUrl_V2));

    if (!announcementResponse) {
      return;
    }

    this.storageService.setLastAnnouncementCheckDate(new Date());
    if (announcementResponse.announcementId) {
      this.storageService.setLastAnnouncement(announcementResponse);
    }
    return new Announcement().deserialize(announcementResponse);
  }

  public markAsSeen(announcement: Announcement): void {
    this.storageService.markAnnouncementAsSeen(announcement);
  }

  ngOnDestroy() {
    this._subscriptions$.forEach((a) => a.unsubscribe());
  }
}
